<template>
  <div class="batch-processing-manager">
    <div class="page-header">
      <h2><i class="fas fa-tasks"></i> Batch Processing Manager</h2>
      <p class="subtitle">Process large volumes of documents for comprehensive analysis</p>
    </div>

    <!-- Current Status Card -->
    <div class="status-card" v-if="currentSession">
      <div class="card-header">
        <h3><i class="fas fa-chart-line"></i> Current Status</h3>
        <span class="status-badge" :class="getStatusClass(currentSession.status)">
          {{ getStatusLabel(currentSession.status) }}
        </span>
      </div>

      <div class="card-body">
        <!-- Progress Bar -->
        <div class="progress-section" v-if="currentSession.status === 'RUNNING'">
          <div class="progress-bar-container">
            <div class="progress-bar" :style="{ width: currentSession.progress_percentage + '%' }"></div>
          </div>
          <div class="progress-text">
            {{ currentSession.progress_percentage.toFixed(1) }}% Complete
          </div>
        </div>

        <!-- Metrics Grid -->
        <div class="metrics-grid">
          <div class="metric">
            <div class="metric-label">Documents</div>
            <div class="metric-value">
              {{ currentSession.documents_processed }} / {{ currentSession.total_documents }}
            </div>
          </div>
          <div class="metric">
            <div class="metric-label">Analyses Created</div>
            <div class="metric-value">{{ currentSession.analyses_created }}</div>
          </div>
          <div class="metric">
            <div class="metric-label">Success Rate</div>
            <div class="metric-value">{{ currentSession.success_rate.toFixed(1) }}%</div>
          </div>
          <div class="metric" v-if="currentSession.status === 'RUNNING'">
            <div class="metric-label">ETA</div>
            <div class="metric-value">{{ formatETA(currentSession.estimated_completion) }}</div>
          </div>
        </div>

        <!-- Performance Metrics (if running) -->
        <div class="performance-metrics" v-if="currentSession.status === 'RUNNING' && currentSession.performance">
          <h4><i class="fas fa-tachometer-alt"></i> Performance</h4>
          <div class="metrics-row">
            <span><strong>Speed:</strong> {{ currentSession.performance.docs_per_minute.toFixed(1) }} docs/min</span>
            <span><strong>Analyses/min:</strong> {{ currentSession.performance.analyses_per_minute.toFixed(0) }}</span>
            <span><strong>Avg Subjects/Doc:</strong> {{ currentSession.performance.avg_subjects_per_doc.toFixed(1) }}</span>
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="recent-activity" v-if="currentSession.recent_activity && currentSession.recent_activity.length > 0">
          <h4><i class="fas fa-list"></i> Recent Activity</h4>
          <div class="activity-list">
            <div v-for="activity in currentSession.recent_activity.slice(0, 5)" :key="activity.document_name" class="activity-item">
              <span class="activity-icon" :class="activity.status === 'COMPLETED' ? 'success' : 'warning'">
                <i :class="activity.status === 'COMPLETED' ? 'fas fa-check' : 'fas fa-exclamation'"></i>
              </span>
              <span class="activity-doc">{{ activity.document_name }}</span>
              <span class="activity-subject">→ {{ activity.subject_name }}</span>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button v-if="currentSession.status === 'RUNNING'" @click="pauseProcessing" class="btn btn-warning">
            <i class="fas fa-pause"></i> Pause
          </button>
          <button v-if="currentSession.status === 'PAUSED'" @click="resumeProcessing" class="btn btn-primary">
            <i class="fas fa-play"></i> Resume
          </button>
          <button @click="refreshStatus" class="btn btn-secondary">
            <i class="fas fa-sync"></i> Refresh
          </button>
          <button v-if="currentSession.status === 'COMPLETED'" @click="viewReport(currentSession.id)" class="btn btn-info">
            <i class="fas fa-file-alt"></i> View Report
          </button>
        </div>
      </div>
    </div>

    <!-- No Active Session -->
    <div class="no-session-card" v-else>
      <div class="empty-state">
        <i class="fas fa-inbox fa-3x"></i>
        <h3>No Active Batch Processing</h3>
        <p>Start a new batch processing session to analyze documents</p>
      </div>
    </div>

    <!-- Configuration Card -->
    <div class="config-card">
      <div class="card-header">
        <h3><i class="fas fa-cog"></i> Configuration</h3>
      </div>

      <div class="card-body">
        <!-- Document Source -->
        <div class="form-group">
          <label>Source Documents</label>
          <div class="radio-group">
            <label class="radio-option">
              <input type="radio" v-model="config.source" value="project68_documents" />
              <span>Project 68 Documents ({{ documentCounts.project68 || 0 }} docs)</span>
            </label>
            <label class="radio-option">
              <input type="radio" v-model="config.source" value="unprocessed_only" />
              <span>Unprocessed Only ({{ documentCounts.unprocessed || 0 }} docs)</span>
            </label>
            <label class="radio-option">
              <input type="radio" v-model="config.source" value="all_documents" />
              <span>All Project Documents ({{ documentCounts.all || 0 }} docs)</span>
            </label>
          </div>
        </div>

        <!-- Processing Options -->
        <div class="options-grid">
          <div class="form-group">
            <label>Batch Size</label>
            <select v-model.number="config.batch_size" class="form-control">
              <option :value="10">10 (Conservative)</option>
              <option :value="25">25 (Balanced)</option>
              <option :value="50">50 (Recommended)</option>
              <option :value="75">75 (Fast)</option>
              <option :value="100">100 (Maximum)</option>
            </select>
            <small>Number of documents to process in parallel</small>
          </div>

          <div class="form-group">
            <label>Max Subjects per Document</label>
            <select v-model.number="config.max_subjects_per_doc" class="form-control">
              <option :value="3">3 (Fast)</option>
              <option :value="5">5 (Recommended)</option>
              <option :value="10">10 (Comprehensive)</option>
              <option :value="15">15 (Maximum)</option>
            </select>
            <small>Maximum subjects to analyze per document</small>
          </div>

          <div class="form-group">
            <label>AI Model</label>
            <select v-model="config.model" class="form-control">
              <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite (Unlimited - Recommended) 🚀</option>
              <option value="gemini-2.5-flash">Gemini 2.5 Flash (Fast & Cheap) ⚡</option>
              <option value="gemini-2.5-pro">Gemini 2.5 Pro (Best Quality) 💎</option>
              <option value="gemini-1.5-flash">Gemini 1.5 Flash (Fallback) 💨</option>
            </select>
            <small>AI model for analysis</small>
          </div>

          <div class="form-group">
            <label>Match Threshold</label>
            <select v-model.number="config.match_threshold" class="form-control">
              <option :value="0.6">0.6 (Lenient)</option>
              <option :value="0.7">0.7 (Balanced)</option>
              <option :value="0.8">0.8 (Strict)</option>
              <option :value="0.9">0.9 (Very Strict)</option>
            </select>
            <small>Minimum confidence for entity matching</small>
          </div>
        </div>

        <!-- Estimates -->
        <div class="estimates-section" v-if="estimates">
          <h4><i class="fas fa-calculator"></i> Estimates</h4>
          <div class="estimates-grid">
            <div class="estimate">
              <span class="estimate-label">Time:</span>
              <span class="estimate-value">~{{ estimates.time_minutes }} minutes</span>
            </div>
            <div class="estimate">
              <span class="estimate-label">Cost:</span>
              <span class="estimate-value">${{ estimates.cost.toFixed(2) }}</span>
            </div>
            <div class="estimate">
              <span class="estimate-label">Analyses:</span>
              <span class="estimate-value">~{{ estimates.analyses.toLocaleString() }}</span>
            </div>
          </div>
        </div>

        <!-- Start Button -->
        <div class="start-section">
          <button 
            @click="startBatchProcessing" 
            class="btn btn-primary btn-lg"
            :disabled="loading || (currentSession && currentSession.status === 'RUNNING')"
          >
            <i class="fas fa-rocket"></i> 
            {{ loading ? 'Starting...' : 'Start Batch Processing' }}
          </button>
          <button @click="showHistory = true" class="btn btn-secondary btn-lg">
            <i class="fas fa-history"></i> View History
          </button>
        </div>
      </div>
    </div>

    <!-- History Modal -->
    <div v-if="showHistory" class="modal-overlay" @click.self="showHistory = false">
      <div class="modal-content large">
        <div class="modal-header">
          <h3><i class="fas fa-history"></i> Batch Processing History</h3>
          <button @click="showHistory = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="loadingHistory" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i> Loading history...
          </div>
          <div v-else-if="history.length === 0" class="empty-state">
            <p>No batch processing history found</p>
          </div>
          <div v-else class="history-list">
            <div v-for="session in history" :key="session.id" class="history-item">
              <div class="history-header">
                <div>
                  <strong>Session #{{ session.id }}</strong>
                  <span class="status-badge" :class="getStatusClass(session.status)">
                    {{ getStatusLabel(session.status) }}
                  </span>
                </div>
                <div class="history-date">{{ formatDate(session.started_at) }}</div>
              </div>
              <div class="history-stats">
                <span><strong>Documents:</strong> {{ session.documents_processed }} / {{ session.total_documents }}</span>
                <span><strong>Analyses:</strong> {{ session.analyses_created }}</span>
                <span><strong>Success Rate:</strong> {{ session.success_rate.toFixed(1) }}%</span>
                <span v-if="session.duration_minutes"><strong>Duration:</strong> {{ session.duration_minutes.toFixed(0) }} min</span>
              </div>
              <div class="history-actions">
                <button @click="viewReport(session.id)" class="btn btn-sm btn-info">
                  <i class="fas fa-file-alt"></i> View Report
                </button>
                <button v-if="session.status === 'RUNNING'" @click="monitorSession(session.id)" class="btn btn-sm btn-primary">
                  <i class="fas fa-eye"></i> Monitor
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Report Modal -->
    <div v-if="showReport" class="modal-overlay" @click.self="showReport = false">
      <div class="modal-content large">
        <div class="modal-header">
          <h3><i class="fas fa-file-alt"></i> Batch Processing Report</h3>
          <button @click="showReport = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="loadingReport" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i> Loading report...
          </div>
          <div v-else-if="report" class="report-content">
            <!-- Summary -->
            <div class="report-section">
              <h4>📊 Summary</h4>
              <div class="metrics-grid">
                <div class="metric">
                  <div class="metric-label">Total Documents</div>
                  <div class="metric-value">{{ report.summary.total_documents }}</div>
                </div>
                <div class="metric">
                  <div class="metric-label">Processed</div>
                  <div class="metric-value">{{ report.summary.documents_processed }}</div>
                </div>
                <div class="metric">
                  <div class="metric-label">Analyses Created</div>
                  <div class="metric-value">{{ report.summary.total_analyses }}</div>
                </div>
                <div class="metric">
                  <div class="metric-label">Success Rate</div>
                  <div class="metric-value">{{ report.summary.success_rate.toFixed(1) }}%</div>
                </div>
              </div>
            </div>

            <!-- Top Subjects -->
            <div class="report-section" v-if="report.top_subjects && report.top_subjects.length > 0">
              <h4>🏆 Top Subjects</h4>
              <div class="top-subjects-list">
                <div v-for="(subject, index) in report.top_subjects.slice(0, 10)" :key="index" class="subject-item">
                  <span class="subject-rank">{{ index + 1 }}</span>
                  <span class="subject-name">{{ subject.subject_name }}</span>
                  <span class="subject-count">{{ subject.analyses_count }} analyses</span>
                </div>
              </div>
            </div>

            <!-- Errors (if any) -->
            <div class="report-section" v-if="report.error_count > 0">
              <h4>⚠️ Errors ({{ report.error_count }})</h4>
              <div class="errors-list">
                <div v-for="(error, index) in report.errors.slice(0, 10)" :key="index" class="error-item">
                  <span class="error-time">{{ formatTime(error.timestamp) }}</span>
                  <span class="error-doc">Doc {{ error.document_id }}</span>
                  <span class="error-message">{{ error.message }}</span>
                </div>
              </div>
            </div>

            <!-- Download Button -->
            <div class="report-actions">
              <button @click="downloadReport" class="btn btn-primary">
                <i class="fas fa-download"></i> Download JSON Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle"></i> {{ error }}
      <button @click="error = null" class="close-btn">&times;</button>
    </div>

    <!-- Success Alert -->
    <div v-if="successMessage" class="alert alert-success">
      <i class="fas fa-check-circle"></i> {{ successMessage }}
      <button @click="successMessage = null" class="close-btn">&times;</button>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'BatchProcessingManager',
  
  props: {
    projectId: {
      type: Number,
      required: true
    }
  },
  
  data() {
    return {
      currentSession: null,
      history: [],
      report: null,
      
      config: {
        source: 'project68_documents',
        batch_size: 50,
        max_subjects_per_doc: 5,
        model: 'gemini-2.5-flash-lite',
        match_threshold: 0.7,
        sequential: false
      },
      
      documentCounts: {
        project68: 0,
        unprocessed: 0,
        all: 0
      },
      
      estimates: null,
      
      loading: false,
      loadingHistory: false,
      loadingReport: false,
      showHistory: false,
      showReport: false,
      error: null,
      successMessage: null,
      
      refreshInterval: null
    }
  },
  
  mounted() {
    this.loadDocumentCounts()
    this.loadCurrentSession()
    this.calculateEstimates()
    
    // Auto-refresh every 5 seconds if there's an active session
    this.refreshInterval = setInterval(() => {
      if (this.currentSession && this.currentSession.status === 'RUNNING') {
        this.refreshStatus()
      }
    }, 5000)
  },
  
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  
  methods: {
    async loadDocumentCounts() {
      try {
        // Project 68 documents are stored in THIS project (e.g., Project 13 in production)
        // They have upload_method='BATCH_LEGACY' and metadata.source_project_id=68
        // 68 is the LEGACY database project ID, not the current project ID
        
        // Get all documents for THIS project
        const allResponse = await api.get(`/projects/${this.projectId}/documents/`, {
          params: { skip_file_urls: true }
        })
        
        const allDocs = allResponse.data.documents || []
        this.documentCounts.all = allDocs.length
        
        // Count Project 68 documents (from legacy database, now in THIS project)
        // Filter by upload_method='BATCH_LEGACY' and metadata.source_project_id=68
        const project68Docs = allDocs.filter(doc => {
          return doc.upload_method === 'BATCH_LEGACY' && 
                 doc.metadata && 
                 (doc.metadata.source_project_id == 68 || doc.metadata.source_project_id === '68')
        })
        this.documentCounts.project68 = project68Docs.length
        
        console.log('Document counts loaded:', {
          projectId: this.projectId,
          all: this.documentCounts.all,
          project68: this.documentCounts.project68,
          sampleDoc: allDocs[0],
          uploadMethods: [...new Set(allDocs.map(d => d.upload_method))],
          hasMetadata: allDocs.filter(d => d.metadata).length
        })
        
        // For unprocessed: estimate as documents without comprehensive analyses
        // Conservative estimate: 80% of Project 68 docs haven't been batch processed yet
        this.documentCounts.unprocessed = Math.floor(this.documentCounts.project68 * 0.8)
        
      } catch (error) {
        console.error('Error loading document counts:', error)
        // Set to 0 if error
        this.documentCounts.all = 0
        this.documentCounts.project68 = 0
        this.documentCounts.unprocessed = 0
      }
    },
    
    async loadCurrentSession() {
      try {
        const response = await api.get(`/projects/${this.projectId}/batch-processing/sessions/`, {
          params: { status: 'RUNNING', page_size: 1 }
        })
        
        if (response.data.sessions && response.data.sessions.length > 0) {
          const session = response.data.sessions[0]
          await this.loadSessionStatus(session.id)
        } else {
          // Check for most recent completed session
          const completedResponse = await api.get(`/projects/${this.projectId}/batch-processing/sessions/`, {
            params: { page_size: 1 }
          })
          if (completedResponse.data.sessions && completedResponse.data.sessions.length > 0) {
            this.currentSession = completedResponse.data.sessions[0]
          }
        }
      } catch (error) {
        console.error('Error loading current session:', error)
      }
    },
    
    async loadSessionStatus(sessionId) {
      try {
        const response = await api.get(`/batch-processing/${sessionId}/status/`)
        if (response.data.success) {
          this.currentSession = {
            id: response.data.session_id,
            status: response.data.status,
            documents_processed: response.data.progress.documents_processed,
            total_documents: response.data.progress.total_documents,
            progress_percentage: response.data.progress.progress_percentage,
            analyses_created: response.data.progress.analyses_created,
            success_rate: response.data.progress.success_rate,
            estimated_completion: response.data.timing.estimated_completion,
            performance: response.data.performance,
            recent_activity: response.data.recent_activity
          }
        }
      } catch (error) {
        console.error('Error loading session status:', error)
      }
    },
    
    calculateEstimates() {
      // Calculate estimates based on config
      const docCount = this.getDocumentCount()
      const avgSubjects = 3
      const totalAnalyses = docCount * avgSubjects
      
      // Time estimate
      let timePerDoc
      if (this.config.batch_size > 1) {
        timePerDoc = (5 * avgSubjects) / this.config.batch_size
      } else {
        timePerDoc = 5 * avgSubjects
      }
      const estimatedMinutes = (timePerDoc * docCount) / 60
      
      // Cost estimate
      const modelCosts = {
        'gemini-2.5-flash-lite': { input: 0.00, output: 0.00 },  // Free tier - unlimited
        'gemini-2.5-flash': { input: 0.075, output: 0.30 },
        'gemini-2.5-pro': { input: 1.25, output: 5.00 },
        'gemini-1.5-flash': { input: 0.075, output: 0.30 }
      }
      const costs = modelCosts[this.config.model]
      const inputCost = (docCount * 5000 * avgSubjects * costs.input) / 1_000_000
      const outputCost = (totalAnalyses * 1000 * costs.output) / 1_000_000
      
      this.estimates = {
        time_minutes: Math.ceil(estimatedMinutes),
        cost: inputCost + outputCost,
        analyses: totalAnalyses
      }
    },
    
    getDocumentCount() {
      if (this.config.source === 'project68_documents') {
        return this.documentCounts.project68
      } else if (this.config.source === 'unprocessed_only') {
        return this.documentCounts.unprocessed
      } else {
        return this.documentCounts.all
      }
    },
    
    async startBatchProcessing() {
      if (!confirm(`Start batch processing for ${this.getDocumentCount()} documents?\n\nEstimated time: ${this.estimates.time_minutes} minutes\nEstimated cost: $${this.estimates.cost.toFixed(2)}`)) {
        return
      }
      
      this.loading = true
      this.error = null
      
      try {
        const response = await api.post(`/projects/${this.projectId}/batch-processing/start/`, {
          source: this.config.source,
          filter: {
            unprocessed_only: this.config.source === 'unprocessed_only'
          },
          config: this.config
        })
        
        if (response.data.success) {
          this.successMessage = 'Batch processing started successfully!'
          await this.loadSessionStatus(response.data.session_id)
        } else {
          this.error = response.data.error || 'Failed to start batch processing'
        }
      } catch (error) {
        console.error('Error starting batch processing:', error)
        this.error = error.response?.data?.error || 'Failed to start batch processing'
      } finally {
        this.loading = false
      }
    },
    
    async refreshStatus() {
      if (this.currentSession) {
        await this.loadSessionStatus(this.currentSession.id)
      }
    },
    
    async pauseProcessing() {
      if (!confirm('Pause batch processing?')) return
      
      try {
        const response = await api.post(`/batch-processing/${this.currentSession.id}/pause/`)
        if (response.data.success) {
          this.successMessage = 'Batch processing paused'
          await this.refreshStatus()
        }
      } catch (error) {
        console.error('Error pausing:', error)
        this.error = 'Failed to pause batch processing'
      }
    },
    
    async resumeProcessing() {
      try {
        const response = await api.post(`/batch-processing/${this.currentSession.id}/resume/`)
        if (response.data.success) {
          this.successMessage = 'Batch processing resumed'
          await this.refreshStatus()
        }
      } catch (error) {
        console.error('Error resuming:', error)
        this.error = 'Failed to resume batch processing'
      }
    },
    
    async loadHistory() {
      this.loadingHistory = true
      try {
        const response = await api.get(`/projects/${this.projectId}/batch-processing/sessions/`)
        if (response.data.success) {
          this.history = response.data.sessions
        }
      } catch (error) {
        console.error('Error loading history:', error)
      } finally {
        this.loadingHistory = false
      }
    },
    
    async viewReport(sessionId) {
      this.showReport = true
      this.loadingReport = true
      
      try {
        const response = await api.get(`/batch-processing/${sessionId}/report/`)
        if (response.data.success) {
          this.report = response.data
        }
      } catch (error) {
        console.error('Error loading report:', error)
        this.error = 'Failed to load report'
      } finally {
        this.loadingReport = false
      }
    },
    
    downloadReport() {
      const dataStr = JSON.stringify(this.report, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `batch_report_${this.report.session_id}.json`
      link.click()
      URL.revokeObjectURL(url)
    },
    
    monitorSession(sessionId) {
      this.showHistory = false
      this.loadSessionStatus(sessionId)
    },
    
    getStatusClass(status) {
      const classes = {
        'RUNNING': 'status-running',
        'COMPLETED': 'status-completed',
        'FAILED': 'status-failed',
        'PAUSED': 'status-paused',
        'CANCELLED': 'status-cancelled'
      }
      return classes[status] || ''
    },
    
    getStatusLabel(status) {
      const labels = {
        'RUNNING': '🔄 Running',
        'COMPLETED': '✅ Completed',
        'FAILED': '❌ Failed',
        'PAUSED': '⏸️ Paused',
        'CANCELLED': '🚫 Cancelled'
      }
      return labels[status] || status
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleString()
    },
    
    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString()
    },
    
    formatETA(etaString) {
      if (!etaString) return 'Calculating...'
      const eta = new Date(etaString)
      const now = new Date()
      const diffMinutes = Math.round((eta - now) / 60000)
      if (diffMinutes < 1) return 'Less than 1 minute'
      if (diffMinutes < 60) return `${diffMinutes} minutes`
      const hours = Math.floor(diffMinutes / 60)
      const mins = diffMinutes % 60
      return `${hours}h ${mins}m`
    }
  },
  
  watch: {
    config: {
      deep: true,
      handler() {
        this.calculateEstimates()
      }
    },
    
    showHistory(val) {
      if (val) {
        this.loadHistory()
      }
    }
  }
}
</script>

<style scoped>
.batch-processing-manager {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 30px;
}

.page-header h2 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

.subtitle {
  color: #7f8c8d;
  margin: 0;
}

/* Status Card */
.status-card, .config-card, .no-session-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.card-header {
  padding: 20px;
  border-bottom: 1px solid #ecf0f1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  color: #2c3e50;
}

.card-body {
  padding: 20px;
}

/* Progress Bar */
.progress-section {
  margin-bottom: 20px;
}

.progress-bar-container {
  height: 30px;
  background: #ecf0f1;
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3498db, #2ecc71);
  transition: width 0.3s ease;
}

.progress-text {
  text-align: center;
  font-weight: bold;
  color: #2c3e50;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin: 20px 0;
}

.metric {
  text-align: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.metric-label {
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.metric-value {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

/* Performance Metrics */
.performance-metrics {
  margin: 20px 0;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.performance-metrics h4 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

.metrics-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.metrics-row span {
  color: #34495e;
}

/* Recent Activity */
.recent-activity {
  margin: 20px 0;
}

.recent-activity h4 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 14px;
}

.activity-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.activity-icon.success {
  background: #2ecc71;
  color: white;
}

.activity-icon.warning {
  background: #f39c12;
  color: white;
}

.activity-doc {
  font-weight: 500;
  color: #2c3e50;
}

.activity-subject {
  color: #7f8c8d;
}

/* Status Badges */
.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-running {
  background: #3498db;
  color: white;
}

.status-completed {
  background: #2ecc71;
  color: white;
}

.status-failed {
  background: #e74c3c;
  color: white;
}

.status-paused {
  background: #f39c12;
  color: white;
}

/* Configuration */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.radio-option:hover {
  background: #f8f9fa;
  border-color: #3498db;
}

.radio-option input[type="radio"] {
  margin: 0;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin: 20px 0;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #2c3e50;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-group small {
  display: block;
  margin-top: 5px;
  color: #7f8c8d;
  font-size: 12px;
}

/* Estimates */
.estimates-section {
  margin: 20px 0;
  padding: 15px;
  background: #e8f5e9;
  border-radius: 8px;
  border-left: 4px solid #2ecc71;
}

.estimates-section h4 {
  margin: 0 0 10px 0;
  color: #27ae60;
}

.estimates-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.estimate {
  display: flex;
  flex-direction: column;
}

.estimate-label {
  font-size: 12px;
  color: #27ae60;
  margin-bottom: 5px;
}

.estimate-value {
  font-size: 18px;
  font-weight: bold;
  color: #27ae60;
}

/* Buttons */
.action-buttons, .start-section {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
}

.btn-secondary {
  background: #95a5a6;
  color: white;
}

.btn-warning {
  background: #f39c12;
  color: white;
}

.btn-info {
  background: #16a085;
  color: white;
}

.btn-lg {
  padding: 12px 24px;
  font-size: 16px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.empty-state i {
  color: #bdc3c7;
  margin-bottom: 20px;
}

.empty-state h3 {
  margin: 10px 0;
  color: #95a5a6;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-content.large {
  max-width: 900px;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #ecf0f1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #7f8c8d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #2c3e50;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
}

/* History List */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.history-item {
  padding: 15px;
  border: 1px solid #ecf0f1;
  border-radius: 8px;
  background: #f8f9fa;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.history-stats {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 14px;
  color: #34495e;
}

.history-actions {
  display: flex;
  gap: 10px;
}

/* Report */
.report-section {
  margin-bottom: 30px;
}

.report-section h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
}

.top-subjects-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.subject-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
}

.subject-rank {
  width: 30px;
  height: 30px;
  background: #3498db;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.subject-name {
  flex: 1;
  font-weight: 500;
}

.subject-count {
  color: #7f8c8d;
  font-size: 14px;
}

/* Alerts */
.alert {
  padding: 15px 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.alert-success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: #7f8c8d;
}

/* Responsive */
@media (max-width: 768px) {
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .options-grid {
    grid-template-columns: 1fr;
  }
  
  .estimates-grid {
    grid-template-columns: 1fr;
  }
  
  .action-buttons, .start-section {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>

